import React, { useState, useEffect } from 'react';
import { CloudUpload, FileText } from 'lucide-react';

const DocumentForm = ({
  formData,
  formErrors,
  selectedDocument,
  onInputChange,
  onSubmit,
  submitting,
  soutenances,
  typesDocuments
}) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onInputChange({
        target: {
          name: 'fichier',
          files: e.dataTransfer.files
        }
      });
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="bg-indigo-50 p-4 rounded-lg">
        <h4 className="text-md font-medium text-indigo-900 mb-3">Informations du document</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Soutenance *
            </label>
            <select
              required
              name="soutenance"
              value={formData.soutenance}
              onChange={onInputChange}
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                formErrors.soutenance ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une soutenance</option>
              {soutenances.map(soutenance => (
                <option key={soutenance.id} value={soutenance.id}>
                  {soutenance.etudiant_principal_info ? 
                    `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom} - ${soutenance.titre_travail.substring(0, 30)}...` : 
                    soutenance.titre_travail
                  }
                </option>
              ))}
            </select>
            {formErrors.soutenance && (
              <p className="text-red-500 text-sm mt-1">{formErrors.soutenance}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de document *
            </label>
            <select
              required
              name="type_document"
              value={formData.type_document}
              onChange={onInputChange}
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                formErrors.type_document ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {typesDocuments.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
            {formErrors.type_document && (
              <p className="text-red-500 text-sm mt-1">{formErrors.type_document}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre du document *
          </label>
          <input
            type="text"
            required
            name="titre"
            value={formData.titre}
            onChange={onInputChange}
            className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              formErrors.titre ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ex: Mémoire de Master en Informatique"
          />
          {formErrors.titre && (
            <p className="text-red-500 text-sm mt-1">{formErrors.titre}</p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fichier {!selectedDocument && '*'}
          </label>
          <div 
            className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
              dragActive 
                ? 'border-indigo-400 bg-indigo-50' 
                : 'border-gray-300 hover:border-indigo-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-1 text-center">
              <CloudUpload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                  <span>Choisir un fichier</span>
                  <input
                    id="file-upload"
                    name="fichier"
                    type="file"
                    className="sr-only"
                    accept=".pdf,.doc,.docx,.ppt,.pptx"
                    onChange={onInputChange}
                  />
                </label>
                <p className="pl-1">ou glisser-déposer</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, DOC, DOCX, PPT, PPTX jusqu'à 50MB
              </p>
              {formData.fichier && (
                <div className="flex items-center justify-center space-x-2 text-sm text-indigo-600 font-medium">
                  <FileText className="h-4 w-4" />
                  <span>Fichier sélectionné: {formData.fichier.name}</span>
                </div>
              )}
            </div>
          </div>
          {formErrors.fichier && (
            <p className="text-red-500 text-sm mt-1">{formErrors.fichier}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={submitting}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {submitting && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{selectedDocument ? 'Modifier' : 'Uploader'}</span>
        </button>
      </div>
    </form>
  );
};

export default DocumentForm; 